import React, { memo, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getSmoothStepPath } from 'reactflow';

type Point = { x: number; y: number };

interface RoutedEdgeData {
  routePoints?: Point[];
  hoverLabel?: string;
  hoverSubLabel?: string;
  displayName?: string;
  sourceCubeCode?: string;
  destinationCubeCode?: string;
}

function buildPath(points: Point[]) {
  if (points.length === 0) {
    return '';
  }

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`;
    }

    return `${path} L ${point.x} ${point.y}`;
  }, '');
}

function getLabelPosition(points: Point[]) {
  if (points.length < 2) {
    return null;
  }

  let totalLength = 0;
  const segments = [];

  for (let index = 0; index < points.length - 1; index += 1) {
    const startPoint = points[index];
    const endPoint = points[index + 1];
    const segmentLength = Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y);

    segments.push({ startPoint, endPoint, segmentLength });
    totalLength += segmentLength;
  }

  if (totalLength === 0) {
    return {
      x: points[0].x,
      y: points[0].y,
    };
  }

  const halfLength = totalLength / 2;
  let traversedLength = 0;

  for (const segment of segments) {
    if (traversedLength + segment.segmentLength >= halfLength) {
      const remainingLength = halfLength - traversedLength;
      const ratio = segment.segmentLength === 0 ? 0 : remainingLength / segment.segmentLength;

      return {
        x: segment.startPoint.x + (segment.endPoint.x - segment.startPoint.x) * ratio,
        y: segment.startPoint.y + (segment.endPoint.y - segment.startPoint.y) * ratio,
      };
    }

    traversedLength += segment.segmentLength;
  }

  return {
    x: points[points.length - 1].x,
    y: points[points.length - 1].y,
  };
}

function CubeRoutedEdge({
  data,
  markerEnd,
  markerStart,
  style,
  interactionWidth,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: EdgeProps<RoutedEdgeData>) {
  const [isHovered, setIsHovered] = useState(false);
  const routePoints = data?.routePoints || [];
  const [fallbackPath, fallbackLabelX, fallbackLabelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 14,
    offset: 24,
  });
  const path = routePoints.length >= 2 ? buildPath(routePoints) : fallbackPath;
  const labelPosition = getLabelPosition(routePoints) || { x: fallbackLabelX, y: fallbackLabelY };
  const hoverLabel =
    data?.hoverLabel ||
    data?.displayName ||
    `${data?.sourceCubeCode || 'Source'} -> ${data?.destinationCubeCode || 'Target'}`;
  const hoverSubLabel = data?.hoverSubLabel;
  const edgeStyle = {
    ...style,
    transition: 'stroke 120ms ease, stroke-width 120ms ease, filter 120ms ease',
    filter: isHovered ? 'drop-shadow(0 0 6px rgba(37,99,235,0.35))' : undefined,
    stroke: isHovered ? '#2563eb' : style?.stroke,
    strokeWidth: isHovered ? 2.4 : style?.strokeWidth,
  };

  return (
    <>
      <BaseEdge
        path={path}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={edgeStyle}
        interactionWidth={interactionWidth}
      />
      <path
        d={path}
        fill='none'
        stroke='transparent'
        strokeWidth={Math.max(interactionWidth || 20, 20)}
        style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      {isHovered && hoverLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelPosition.x}px, ${labelPosition.y}px)`,
              pointerEvents: 'none',
              background: '#0f172a',
              color: '#ffffff',
              padding: '6px 9px',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.2,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(15,23,42,0.24)',
              border: '1px solid rgba(148,163,184,0.28)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <span>{hoverLabel}</span>
            {hoverSubLabel && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: 0.35,
                  color: '#cbd5e1',
                }}
              >
                {hoverSubLabel}
              </span>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export default memo(CubeRoutedEdge);
